/**
 * Skeleton Navigation -- instant loading placeholders for Turbo Drive
 * ===================================================================
 *
 * On navigation tap, the skeleton for the destination page is written straight
 * into the LIVE page, synchronously, inside the tap. It paints on the next
 * frame, and Turbo's body swap replaces it with real content the moment the
 * response arrives. Instant is the whole point of this file: nothing may sit
 * between the tap and the first thing that moves.
 *
 * That rules out the two things that look like they belong here:
 *  * Turbo's View Transition. Turbo starts it only when it renders, waits a
 *    frame first, and will not render the real page until it has finished. The
 *    view-transition meta is removed for skeleton visits and put back on
 *    turbo:load, so the response always swaps in immediately.
 *  * A skeleton built as a Turbo snapshot and previewed by Turbo. Cloning,
 *    serialising and re-rendering a whole page cost 100 ms to over a second on a
 *    Pixel 5 before anything moved.
 *
 * The one motion kept is the list card growing into the detail banner (they
 * share view-transition-name entity-hero). This file starts that transition
 * itself, from the tap, around the skeleton injection, so the morph begins
 * after a single captured frame. A tap with no card to morph (tabs, deep links)
 * just cuts to the skeleton.
 *
 * The OUTGOING page is cached here synchronously before the skeleton replaces
 * it, so back pops to it instantly (and its banner shrinks back into the row).
 * The rest of the cache is cleared first, or Turbo would preview a cached copy
 * of the destination over the skeleton. Turbo's own async snapshot of the
 * outgoing page would capture the skeleton, so a turbo-cache-control=no-cache
 * meta makes that copy a no-op for this visit.
 *
 * A hidden tab gets no View Transition either. Pages are still rendered there
 * (page-check.js's refresh, a live update), and the browser refuses a
 * transition while the tab is hidden, a rejection Turbo leaves unhandled. The
 * meta stays stashed while the tab is hidden and comes back when it is shown.
 *
 * Flow:
 *   turbo:before-visit  --> cache outgoing page; disable Turbo's VT;
 *                           inject skeleton (inside the hero morph when there is one)
 *   turbo:before-render --> add .skeleton-reveal to dynamic content areas
 *   turbo:load          --> restore the VT meta (unless hidden), drop the
 *                           no-cache meta, reset
 *   visibilitychange    --> stash the VT meta when hidden, restore it when shown
 */

(function () {
    'use strict';

    // Guard against duplicate initialization (Turbo re-evaluates scripts)
    if (document.documentElement.dataset.skeletonNavInit) return;
    document.documentElement.dataset.skeletonNavInit = 'true';

    var SKELETON_MAP = {
        '/':          'skeleton-dashboard',
        '/dashboard': 'skeleton-dashboard',
        '/quotes':    'skeleton-quotes',
        '/jobs':      'skeleton-jobs',
        '/invoices':  'skeleton-invoices',
        '/clients':   'skeleton-clients'
    };

    // Detail pages carry a record id (/quote/123, /job/45, /invoice/9,
    // /client/7) so they can't live in the static map. Each entity gets its
    // own #skeleton-detail-<type> template (see components/skeletons.html);
    // an unrecognized/deep-link URL falls back to the generic #skeleton-detail.
    // entityForDetailPath (powersync-entities.js, loaded earlier in base.html)
    // is anchored to the bare detail route so sub-paths (e.g. /quote/123/new,
    // /quote/123/edit) don't match, and is the SSOT for the entity list — this
    // file no longer re-types it.
    var entityForDetailPath = window.entityForDetailPath;

    var visitStartTime = null;
    var skeletonActive = false;
    var vtMeta = null; // stashed <meta name="view-transition"> element
    var cacheMetaInjected = false; // did we add a turbo-cache-control meta?

    // Turbo transitions only when the live page carries the view-transition
    // meta, so taking it out of the head turns Turbo's transition off. A stash
    // already held is kept when there is no live meta to take.
    function stashVtMeta() {
        var liveVtMeta = document.querySelector('meta[name="view-transition"]');
        if (liveVtMeta) {
            vtMeta = liveVtMeta;
            liveVtMeta.remove();
        }
    }

    // Turbo's head merge keeps the page's own copy of the meta when a rendered
    // response carries one; the stash goes back only if it did not.
    function restoreVtMeta() {
        if (!vtMeta) return;
        if (!document.querySelector('meta[name="view-transition"]')) document.head.appendChild(vtMeta);
        vtMeta = null;
    }

    // ── Shared skeleton DOM injection ───────────────────────────────
    // Clones a skeleton <template> into the live page: the whole #page-header-zone
    // is cleared and the skeleton's .dashboard-header (if any) dropped in, then the
    // remaining body fills .container.mt-4. Clearing the entire zone (not just the
    // banner) is what stops a detail page's actions card from bleeding through.
    // Used by the full-visit path (turbo:before-visit) below.
    function injectSkeleton(template, container) {
        var frag = template.content.cloneNode(true);
        var skelHeader = frag.querySelector('.dashboard-header');

        // Clear the ENTIRE page-header zone, not just the .dashboard-header banner.
        // Detail pages render a banner PLUS an actions card (and inline demo banner)
        // as siblings in this zone; replacing only the banner left those action
        // buttons on screen, bleeding the old page into the destination skeleton.
        var zone = document.getElementById('page-header-zone');
        if (zone) {
            // offline-indicator.js parks the offline bar inside the zone, under
            // the banner. Move it out first so clearing does not destroy it: the
            // bar carries into the skeleton (re-placed on skeleton:shown) and
            // Turbo's permanent-element swap carries it into the real page.
            var offlineBar = zone.querySelector('#offline-indicator');
            if (offlineBar) zone.after(offlineBar);
            zone.innerHTML = '';
            if (skelHeader) zone.appendChild(skelHeader);
        } else if (skelHeader) {
            // Fallback for any page rendered without the zone wrapper.
            var liveHeader = document.querySelector('.dashboard-header');
            if (liveHeader && liveHeader.parentNode) {
                liveHeader.parentNode.replaceChild(skelHeader, liveHeader);
            } else {
                container.parentNode.insertBefore(skelHeader, container);
            }
        }

        container.innerHTML = '';
        container.appendChild(frag);
        window.scrollTo(0, 0);
    }

    // ── Shared-element hero morph ───────────────────────────────────
    // The tapped card and the destination banner share view-transition-name
    // entity-hero (the banner carries it via .vt-entity-hero, real and skeleton
    // alike, see _entity_banner.html + motion.css), so the push grows the card
    // into the banner and the pop shrinks the banner back into its row. Exactly
    // one row is ever named: stamped AFTER the outgoing snapshot is taken (so no
    // cached page carries the name) and cleared on turbo:load. The lookup is the
    // same eye-link scope readCardData uses, so card and echo agree by construction.
    // The push morph is started by the tap handler below, not by Turbo; the pop
    // morph is Turbo's own transition on the restore visit.
    var HERO_NAME = 'entity-hero';
    var lastPathname = window.location.pathname; // the page being left on a pop

    function stampHeroRow(root, pathname) {
        var link = root.querySelector('.mobile-card-header a[href="' + pathname + '"]');
        var row = link && link.closest('tr[id]');
        if (row) row.style.viewTransitionName = HERO_NAME;
        return !!row;
    }

    function clearHeroRows(root) {
        root.querySelectorAll('tr[id]').forEach(function (row) {
            if (row.style.viewTransitionName) row.style.viewTransitionName = '';
        });
    }

    // ── Layer-3 echo: pre-fill the detail skeleton from the tapped card ──
    // Every list card carries the values the detail header already shows (id,
    // client, status, description as DOM text; address as the eye link's
    // data-nav-address — the exact banner string). The destination URL uniquely
    // identifies the source card via its "eye" link, so we read straight from the
    // live card DOM, no click listener. MUST run BEFORE injectSkeleton (which wipes
    // the list container). Scope the lookup to .mobile-card-header: the desktop
    // action cell links to the SAME detail URL, so an unscoped query could match it
    // and miss the card (and its data-nav-address) entirely.
    function readCardData(pathname) {
        var link = document.querySelector('.mobile-card-header a[href="' + pathname + '"]');
        var card = link && link.closest('.mobile-card-header');
        if (!card) return null;

        var typeEl = card.querySelector('.entity-type');
        var numEl = card.querySelector('.entity-num');
        var nameEl = card.querySelector('.mobile-card-name strong');
        var descEl = card.querySelector('.mobile-card-description');

        // Status badge: the first .mobile-card-meta-top child that isn't the amount.
        var metaTop = card.querySelector('.mobile-card-meta-top');
        var badge = metaTop && Array.prototype.find.call(metaTop.children, function (c) {
            return !c.classList.contains('mobile-card-amount');
        });

        var text = function (el) { return el ? el.textContent.trim() : ''; };
        // The card badge carries its colour as a bg-* class (bg-success, etc.).
        var colorMatch = badge && badge.className.match(/\bbg-(\S+)/);
        return {
            title: (typeEl && numEl) ? (text(typeEl) + ' ' + text(numEl)) : '',
            client: text(nameEl),
            // Exact banner string (site_address_for_navigation), carried on the eye
            // link by _mobile_card_header.html — NOT scraped from the card's display
            // address (which uses a different, multi-line representation that wraps
            // differently). Mirroring needs the identical source value.
            address: (link.dataset.navAddress || '').trim(),
            description: text(descEl),
            statusText: text(badge),
            statusColor: colorMatch ? colorMatch[1] : ''
        };
    }

    // Drop echoed values into the skeleton's data-skel slots. Unfilled slots keep
    // their shimmer (deep links / back-forward have no source card to read).
    // `root` scopes the lookup: defaults to the live document (full-visit path), but
    // the swipe-to-view controller passes its off-screen overlay so the same echo
    // fills its cloned skeleton instead.
    function fillSkeleton(data, root) {
        root = root || document;
        ['title', 'client', 'description'].forEach(function (key) {
            var value = data[key];
            if (!value) return;
            var el = root.querySelector('[data-skel="' + key + '"]');
            if (!el) return;
            el.textContent = value;
            el.classList.remove('skeleton-bar');
            el.removeAttribute('style');
        });
        // Status: only recolour + relabel the badge. Its box is already the real
        // status-badge component (rendered server-side by entity_banner in
        // skeleton mode), so the height matches the loaded banner by construction
        // — we must NOT replace the element or change its sizing.
        var statusBadge = root.querySelector('[data-skel="status"] .badge');
        if (statusBadge) {
            if (data.statusText) statusBadge.textContent = data.statusText;
            if (data.statusColor) {
                statusBadge.className = statusBadge.className.replace(/\bbg-\S+/, 'bg-' + data.statusColor);
            }
        }
    }

    // Resolve the banner's address line from the echoed card value. The line + Navigate
    // button ship as static skeleton chrome, so we never shimmer here — the card already
    // tells us the address:
    //   - address present -> fill the text; drop the button unless this is a quote/job
    //                        (only those carry a Navigate button on the real banner)
    //   - address absent  -> remove the whole line (no empty line, no shimmer)
    function resolveBannerAddress(address, type, root) {
        root = root || document;
        var line = root.querySelector('[data-skel="address-line"]');
        if (!line) return;
        if (!address) { line.remove(); return; }
        var slot = line.querySelector('[data-skel="address"]');
        if (slot) slot.textContent = address;
        if (type !== 'quote' && type !== 'job') {
            var btn = line.querySelector('[data-skel="navigate"]');
            if (btn) btn.remove();
        }
    }

    // ── Shared skeleton builder for overlays ────────────────────────
    // ONE operation that clones the skeleton template for `url` (a list from
    // SKELETON_MAP, the per-entity #skeleton-detail-<type>, or the generic
    // #skeleton-detail for an unrecognized/deep-link URL), lays it out (banner
    // edge-to-edge, body in a .container.mt-4), AND prefills a detail skeleton
    // from the originating card — the same echo the full-visit path uses. The
    // swipe-to-view overlay and the edge-swipe-back underlay both call this and
    // get identical behaviour for free; nobody re-specifies the prefill. `root`
    // is the element to build into; `url` identifies the page. Returns true on
    // success.
    function buildSkeletonFor(url, root) {
        var path;
        try { path = new URL(url || '', window.location.href).pathname; } catch (_e) { path = (url || '').split('?')[0]; }
        var entityType = entityForDetailPath(path);
        var tpl = (SKELETON_MAP[path] && document.getElementById(SKELETON_MAP[path])) ||
            (entityType && document.getElementById('skeleton-detail-' + entityType)) ||
            document.getElementById('skeleton-detail');
        if (!tpl) return false;
        var frag = tpl.content.cloneNode(true);
        var header = frag.querySelector('.dashboard-header');
        if (header) root.appendChild(header);            // moves it out of the fragment
        var container = document.createElement('div');
        container.className = 'container mt-4';
        container.appendChild(frag);                      // the remaining skeleton nodes
        root.appendChild(container);

        var data = readCardData(path);
        if (data) {
            fillSkeleton(data, root);
            resolveBannerAddress(data.address, entityType || '', root);
        }
        return true;
    }

    // ── Show skeleton instantly on tap ──────────────────────────────
    document.addEventListener('turbo:before-visit', function (event) {
        // A visit something else cancelled (turbo-compat.js's repeat-click
        // throttle, which loads before this file) never happens: touching the
        // cache or the page for it would leave a skeleton with nothing to replace it.
        if (event.defaultPrevented) return;

        // A gesture overlay (swipe-to-view, edge-swipe-back) owns this visit's
        // visuals: stand down so no skeleton snapshot competes with it. The same
        // flag makes turbo-navigation.js stamp the visit kind none.
        if (document.documentElement.hasAttribute('data-nav-gesture')) return;

        visitStartTime = performance.now();

        var dest = new URL(event.detail.url);
        var destEntityType = entityForDetailPath(dest.pathname);
        var templateId = SKELETON_MAP[dest.pathname] ||
            (destEntityType ? 'skeleton-detail-' + destEntityType : null);
        if (!templateId) return;

        // Skip if navigating to the current page
        var cur = window.location.pathname;
        if (dest.pathname === cur ||
            (dest.pathname === '/dashboard' && cur === '/') ||
            (dest.pathname === '/' && cur === '/dashboard')) {
            return;
        }

        // Per-entity template may not exist (defensive) -- fall back to the
        // generic detail skeleton rather than skipping the skeleton entirely.
        var template = document.getElementById(templateId) || document.getElementById('skeleton-detail');
        if (!template) return;

        var container = document.querySelector('.container.mt-4');
        if (!container) return;

        var view = (typeof Turbo !== 'undefined' && Turbo.session) ? Turbo.session.view : null;
        if (!view || !view.snapshotCache || !Turbo.PageSnapshot) return;

        // Read the originating card's values from the live list DOM. MUST run
        // before injectSkeleton wipes the list.
        var cardData = destEntityType ? readCardData(dest.pathname) : null;

        // 1. Cache the OUTGOING real page now, synchronously, so back pops to it.
        //    This is the single writer of that snapshot for this visit. Fire
        //    turbo:before-cache first, exactly as Turbo does before its own
        //    snapshot, so controllers strip vendor widgets (Choices, modals,
        //    maps) that would re-wrap or fail to tear down on restore. The cache
        //    is emptied first: a cached destination would be previewed over the
        //    skeleton, and rendering a whole cached page is the slow path.
        //    Two outgoing pages are not cached: a skeleton (a second tap before
        //    the first page landed; the real page was cached by the first tap,
        //    and the URL has not moved yet, so caching now would store the
        //    shimmer where Back looks), and a page that opted out of Turbo's
        //    cache with turbo-cache-control=no-cache, as Turbo itself honours.
        if (!skeletonActive) {
            var outgoing = Turbo.PageSnapshot.fromElement(document.documentElement);
            Turbo.session.notifyApplicationBeforeCachingSnapshot();
            view.snapshotCache.clear();
            if (outgoing.isCacheable) {
                view.snapshotCache.put(new URL(window.location.href), outgoing.clone());
            }
        }

        // 2. Make Turbo's own (async, next-tick) snapshot of the outgoing page a
        //    no-op: it clones after the skeleton is in and would overwrite step 1
        //    with it. Injected AFTER step 1 so the cached head does not carry it;
        //    turbo:load removes it, and Turbo's head merge drops it anyway when
        //    the real page lands.
        if (!document.querySelector('meta[name="turbo-cache-control"]')) {
            var cacheMeta = document.createElement('meta');
            cacheMeta.name = 'turbo-cache-control';
            cacheMeta.content = 'no-cache';
            document.head.appendChild(cacheMeta);
            cacheMetaInjected = true;
        }

        // 3. Turbo must not wrap the real render in its own View Transition:
        //    that waits a frame, then holds the render for the whole animation.
        //    Turbo only transitions when BOTH pages carry the meta, so removing
        //    it here is enough; turbo:load puts it back for back/forward. A
        //    second tap finds it already stashed and must keep that stash.
        stashVtMeta();

        // Subtle haptic on tap. The web layer owns WHEN a nav tap buzzes (same
        // feedback for the bottom nav and the list "eye" links); the ChippieBridge
        // facade owns HOW per runtime (iOS Strada / Android ChippieNative / PWA
        // navigator.vibrate). Guard for the brief window before the haptic bridge
        // controller has registered the impl on the facade.
        if (window.ChippieBridge && window.ChippieBridge.haptic) {
            try { window.ChippieBridge.haptic(); } catch { /* facade gone */ }
        }

        function showSkeleton() {
            injectSkeleton(template, container);
            if (cardData) fillSkeleton(cardData);
            if (destEntityType) resolveBannerAddress(cardData ? cardData.address : '', destEntityType);
            skeletonActive = true;
        }

        // 4. Show the skeleton. A tapped card is named entity-hero and the
        //    skeleton is swapped in inside a View Transition started right here,
        //    so the card begins growing into the banner at the next frame. Any
        //    other tap cuts straight to the skeleton, painted at the next frame.
        var heroRow = destEntityType && typeof document.startViewTransition === 'function' &&
            stampHeroRow(document, dest.pathname);
        if (heroRow) {
            morphTransition = startHeroMorph(showSkeleton);
        } else {
            showSkeleton();
            requestAnimationFrame(announceSkeleton);
        }
    });

    // The root holds still during the hero morph (html[data-skeleton-morph] in
    // motion.css): only the card moves. The transition ends early if the page
    // is hidden or two elements share the name; the skeleton still goes in,
    // because the browser runs the update callback either way.
    var morphTransition = null;

    function startHeroMorph(showSkeleton) {
        var html = document.documentElement;
        html.setAttribute('data-skeleton-morph', '');
        var transition = document.startViewTransition(function () {
            showSkeleton();
            announceSkeleton();
        });
        transition.ready.catch(function () { /* skipped: skeleton is already in */ });
        transition.finished.finally(function () {
            html.removeAttribute('data-skeleton-morph');
            if (morphTransition === transition) morphTransition = null;
        });
        return transition;
    }

    // Tap-to-skeleton timing for rum-timing.js: the skeleton is on screen.
    function announceSkeleton() {
        document.dispatchEvent(new CustomEvent('skeleton:shown'));
    }

    // ── Fade in dynamic content when replacing skeleton ─────────────
    document.addEventListener('turbo:before-render', function (event) {
        // Pop: the outgoing detail banner is named; name the row it came from in
        // the incoming list so the banner shrinks back into it. A list without
        // that row (filtered, deep link) just lets the banner crossfade out.
        if (event.detail.newBody &&
            document.documentElement.getAttribute('data-nav-kind') === 'pop' &&
            document.querySelector('.vt-entity-hero')) {
            stampHeroRow(event.detail.newBody, lastPathname);
        }

        // A response that beats the hero morph waits for it: swapping the banner
        // out mid-flight would blank the morphing element. The wait is at most
        // the rest of one --dur-slow animation.
        if (morphTransition) {
            event.preventDefault();
            morphTransition.finished.finally(function () { event.detail.resume(); });
        }

        if (!visitStartTime) return;

        // Fade in only the dynamic content (data that replaced skeleton bars).
        // The class comes off once the fade has played: left on, it rides into
        // the page's snapshot and saved copy and the fade replays on restore.
        if (skeletonActive && event.detail.newBody) {
            event.detail.newBody.querySelectorAll('.stat-number, .section-body').forEach(function (el) {
                el.classList.add('skeleton-reveal');
                el.addEventListener('animationend', function () {
                    el.classList.remove('skeleton-reveal');
                }, { once: true });
            });
        }
    });

    // ── Page ready: re-enable View Transitions, reset state ────────
    document.addEventListener('turbo:load', function () {
        // The hero name lives only for the transition that used it.
        clearHeroRows(document);
        lastPathname = window.location.pathname;

        // A page rendered in a hidden tab brings its own meta back with it.
        if (document.hidden) stashVtMeta();
        else restoreVtMeta();

        // Remove our no-cache meta (belt and braces: Turbo's head merge already
        // drops it when the real page renders). Only touches the meta WE
        // injected -- pages that ship their own (billing, calendar) are left alone.
        if (cacheMetaInjected) {
            var staleCacheMeta = document.querySelector('meta[name="turbo-cache-control"]');
            if (staleCacheMeta) staleCacheMeta.remove();
            cacheMetaInjected = false;
        }

        if (!visitStartTime) return;
        visitStartTime = null;
        skeletonActive = false;
    });

    // ── Hidden tab: no View Transition ─────────────────────────────
    // A skeleton visit still in flight keeps its stash until turbo:load.
    if (document.hidden) stashVtMeta();
    document.addEventListener('visibilitychange', function () {
        if (document.hidden) stashVtMeta();
        else if (!visitStartTime) restoreVtMeta();
    });

    // Expose the shared skeleton builder for gesture layers: the swipe-to-view
    // overlay (buildDetailSkeleton, kept as the name that controller calls) and
    // the edge-swipe-back underlay (buildSkeletonFor) get the identical,
    // prefilled skeleton.
    window.ChippieSkeleton = { buildSkeletonFor: buildSkeletonFor, buildDetailSkeleton: buildSkeletonFor };
})();
